import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CaError, CaFormFieldComponent, CaLabel } from '@global-front-components/ui/form-field';
import { CaInputDirective } from '@global-front-components/ui/input';
import { TranslateModule } from '@ngx-translate/core';
import { Domicilio, Localidad, Provincia, TipoVia } from '../../model/poliza.model';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DatosDomicilioConfig, ModoEdicion } from '../../../ui/model/catalogo-config.model';
import { CatalogoGeneralStoreService } from '../../services/store/catalogo-general-store.service';
import { CaSelectComponent } from '@global-front-components/ui/select';
import { ModoEdicionUtils } from '../../util/modo-edicion.utils';

@Component({
	selector: 'app-datos-domicilio',
	standalone: true,
	imports: [
		CommonModule,
		CaLabel,
		CaInputDirective,
		CaFormFieldComponent,
		CaError,
		CaSelectComponent,
		ReactiveFormsModule,
		TranslateModule
	],
	templateUrl: './datos-domicilio.component.html',
	styleUrls: ['./datos-domicilio.component.scss']
})
export class DatosDomicilioComponent implements OnInit {
	@Input() config: DatosDomicilioConfig;
	@Input() form: FormGroup;
	@Output() domicilioChange = new EventEmitter<Domicilio>();

	prefijoPersona: string;
	tiposVia: TipoVia[] = [];
	provincias: Provincia[] = [];
	localidades: Localidad[] = [];

	mostrarVia = false;
	mostrarLocalidad = false;
	mostrarProvincia = false;
	modoEdicion: ModoEdicion;

	constructor(
		private catalogoGeneralStore: CatalogoGeneralStoreService,
		private fb: FormBuilder) {
	}

	ngOnInit(): void {
		this.prefijoPersona = this.config?.rol ? `${this.config.rol}_` : '';
		this.mostrarLocalidad = this.config?.mostrarLocalidad;
		this.mostrarProvincia = this.config?.mostrarProvincia;
		this.mostrarVia = this.config?.mostrarVia;
		this.modoEdicion = this.config?.modoEdicion || ModoEdicion.SI;
		if (!this.form) {
			this.form = this.fb.group({});
		}
		
		this.buildForm();
		if (this.config?.mostrarVia) {
			this.catalogoGeneralStore.getTiposVia().subscribe((tiposVia) => {
				if (tiposVia && tiposVia.length > 0) {
					this.tiposVia = tiposVia;
					const tipoVia: TipoVia = this.tiposVia.find(tv => tv.descTipoVia === this.config?.domicilio?.tipoVia) || {};
					if (tipoVia?.codTipoVia) {
						const tipoViaControl = this.form.get(this.prefijoPersona + 'tipo-via');
						tipoViaControl?.setValue(tipoVia.codTipoVia);
						if (this.modoEdicion !== 'SI') {
							tipoViaControl?.disable();
						}
					}
				} else {
					this.catalogoGeneralStore.getTiposVia();
				}
			});
		}
		if (this.config?.mostrarProvincia) {
			this.catalogoGeneralStore.getProvincias().subscribe((provincias) => {
				if (provincias && provincias.length > 0) {
					this.provincias = provincias;
					const provincia: Provincia = this.provincias.find(p => p.descProvincia === this.config?.domicilio?.nombreProvincia) || {};
					if (provincia?.idProvincia) {
						const provinciaControl = this.form.get(this.prefijoPersona + 'provincia');
						provinciaControl?.setValue(provincia.idProvincia);
						if (this.modoEdicion !== 'SI') {
							provinciaControl?.disable();
						}
					}

				} else {
					this.catalogoGeneralStore.getProvincias();
				}
			});
		}

		this.form.valueChanges.subscribe(() => {
			this.emitirDomicilio();
		});
	}

	buildForm() {
		const domicilio = this.config?.domicilio || {};

		if (this.config?.mostrarVia) {
			this.form.addControl(this.prefijoPersona + 'tipo-via',
				this.fb.control(
					{ value: -1, disabled: ModoEdicionUtils.campoDeshabilitado(this.modoEdicion, null) }
				));
			this.form.addControl(this.prefijoPersona + 'nombre-via',
				this.fb.control(
					{ value: domicilio.nombreVia || '', disabled: ModoEdicionUtils.campoDeshabilitado(this.modoEdicion, domicilio.nombreVia) }
				));
			this.form.addControl(this.prefijoPersona + 'numero-via',
				this.fb.control(
					{ value: domicilio.numeroVia || '', disabled: ModoEdicionUtils.campoDeshabilitado(this.modoEdicion, domicilio.numeroVia) }
				));
		}

		if (this.config?.mostrarLocalidad) {

			const valCodPostal = domicilio.codigoPostal || '';
			if (valCodPostal !== '') {
				this.catalogoGeneralStore.getLocalidadesPorCP(valCodPostal).subscribe((localidades) => {
					this.cargarLocalidadesYActualizarForm(localidades);
				});
			}

			this.form.addControl(this.prefijoPersona + 'cod-postal',
				this.fb.control(
					{ value: valCodPostal, disabled: ModoEdicionUtils.campoDeshabilitado(this.modoEdicion, domicilio.codigoPostal) }
				));

			this.form.addControl(this.prefijoPersona + 'localidad',
				this.fb.control(
					{ value: -1 || '', disabled: ModoEdicionUtils.campoDeshabilitado(this.modoEdicion, null) }
				));
		}
		if (this.config?.mostrarProvincia) {
			this.form.addControl(this.prefijoPersona + 'provincia',
				this.fb.control(
					{ value: -1, disabled: ModoEdicionUtils.campoDeshabilitado(this.modoEdicion, null) }
				));
		}
	}

	cambioCodPostal(codPostalEvent: any): void {
		const codPostal = codPostalEvent.target?.value;
		if (codPostal) {
			this.catalogoGeneralStore.getLocalidadesPorCP(codPostal).subscribe((localidades) => {
				this.cargarLocalidadesYActualizarForm(localidades);
			});
		}

	}

	private cargarLocalidadesYActualizarForm(localidades: Localidad[]): void {
		if (localidades?.length > 0){
			this.localidades = localidades;

			const localidad = localidades.find(loc => loc.descPoblacion === this.config?.domicilio?.nombreLocalidad);
			const localidadControl = this.form.get(this.prefijoPersona + 'localidad');
			const provinciaControl = this.form.get(this.prefijoPersona + 'provincia');

			if (localidad) {
				localidadControl?.setValue(localidad.codPoblacion);
				if (this.modoEdicion !== 'SI') {
					localidadControl?.disable();
				}
			}

			const primeraLoc = localidades[0];
			const descProvLocalidad = primeraLoc.descProvincia;
			const provObj = this.provincias.find(p => p.descProvincia === descProvLocalidad);

			provinciaControl?.setValue(provObj?.idProvincia || '');
			provinciaControl?.disable();
		}
	}

	emitirDomicilio(): void {

		const provincia = this.provincias.find(p => p.idProvincia === this.form.get(this.prefijoPersona + 'provincia')?.value);
		const localidad = this.localidades.find(l => l.codPoblacion === this.form.get(this.prefijoPersona + 'localidad')?.value);
		const tipoVia = this.tiposVia.find(tv => tv.codTipoVia === this.form.get(this.prefijoPersona + 'tipo-via')?.value);

		const domicilio: Domicilio = {
			tipoVia: tipoVia?.descTipoVia || '',
			nombreVia: this.form.get(this.prefijoPersona + 'nombre-via')?.value || '',
			numeroVia: this.form.get(this.prefijoPersona + 'numero-via')?.value || '',
			codigoPostal: this.form.get(this.prefijoPersona + 'cod-postal')?.value || '',
			nombreLocalidad: localidad?.descPoblacion || '',
			nombreProvincia: provincia?.descProvincia || '',
			codLocalidad: localidad?.codPoblacion || '',
			codProvincia: String(provincia?.idProvincia || '')
		};

		this.domicilioChange.emit(domicilio);
	}

}
